import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './assets/styles/index.css'
// Phase 1 new style layers (order: tokens -> legacy general -> utilities)
import './assets/styles/tokens.css';
// general.css merged into utilities.css (see utilities.css legacy section)
import './assets/styles/utilities.css';

// Leaflet CSS imports - Important for Webpack bundling
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
// import 'react-leaflet-markercluster/dist/styles.min.css';

// Date formats are initialized lazily where needed to avoid unintended requests.

import App from './App'

import i18n from './i18n';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import configureStore from './store/redux/store/store';
import { fetchAnyUserWithVariableScheduleRule } from './store/redux/actions/userActionCreator';
import { generatePalette, setCSSColorVariables } from './utils';
import { setupAuthLifecycle } from './utils/authLifecycle';
import { isPublicRoute } from './utils/publicRoutes';

// Free-tier feature_locked rejections come back from the request interceptor as
// plain objects (e.g. { error: 'feature_locked', module: 'attendance', ... }).
// The interceptor already shows the user a single toast — but page-level fetches
// that don't catch (and there are many in the locked pages we still mount under
// LockedShell) bubble the raw object up as unhandled rejections, which show as
// `[object Object]` in console and trigger webpack-dev-server's red overlay.
// We swallow those here. Every other rejection passes through untouched so real
// bugs stay loud.
try {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { error?: unknown; errors?: unknown } | undefined;
    if (!reason || typeof reason !== 'object') return;
    const topLevel = (reason as { error?: unknown }).error === 'feature_locked';
    const inErrors =
      Array.isArray((reason as { errors?: unknown }).errors) &&
      ((reason as { errors: Array<{ code?: unknown }> }).errors.some(
        (e) => e?.code === 'feature_locked' || e?.code === 'FeatureLocked',
      ));
    if (topLevel || inErrors) {
      event.preventDefault();
    }
  });
} catch {}

const { store, persistor } = configureStore();
// Expose the Redux store + persistor for utility modules (e.g., axios
// interceptors, forceLogout) so they don't have to recreate them.
try {
  (globalThis as any).__APP_STORE__ = store;
  (globalThis as any).__APP_PERSISTOR__ = persistor;
} catch {}

// Wire UserManager events → Redux, plus cross-tab logout sync. Must run
// BEFORE i18n.loadNamespaces() so that any signinCallback / signinSilent
// that fires during React's initial render isn't dispatched into an
// unsubscribed event emitter.
try {
  setupAuthLifecycle(store, persistor!);
} catch (e) {
  console.error('[auth] setupAuthLifecycle failed:', e);
}
// Kick off initial fetches needed app-wide, but only after an access token exists
// AND the current route actually needs an authenticated user. On public routes
// (e.g. /p/<slug>), a stale token left in localStorage from a prior session
// would otherwise 401 this request and the response interceptor would force
// the anonymous visitor to /Account/Login — see utils/publicRoutes.ts.
try {
  if (isPublicRoute()) {
    // Intentional no-op on public pages.
  } else {
  const hasAccessToken = (() => {
    try {
      const persisted = localStorage.getItem('persist:root');
      if (persisted) {
        const root = JSON.parse(persisted);
        if (root && root.user) {
          const userState = JSON.parse(root.user);
          if (userState?.userAuthInfo?.access_token) return true;
        }
      }
      // Fallback: check OIDC storage directly
      const oidcKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('oidc.user:') || (key.startsWith('oidc.') && localStorage.getItem(key)?.includes('access_token'))
      );
      for (const key of oidcKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) return true;
        } catch {}
      }
    } catch {}
    return false;
  })();
  if (hasAccessToken) {
    store.dispatch<any>(fetchAnyUserWithVariableScheduleRule());
  }
  }
} catch {}

// Set initial document direction based on persisted Redux rtl flag (if present) before app mounts
try {
  const persisted = localStorage.getItem('persist:root');
  if (persisted && typeof document !== 'undefined') {
    const root = JSON.parse(persisted);
    if (root && root.user) {
      const userState = JSON.parse(root.user);
      if (typeof userState?.rtl === 'boolean') {
        const dir = userState.rtl ? 'rtl' : 'ltr';
        if (document.documentElement.getAttribute('dir') !== dir) {
          document.documentElement.setAttribute('dir', dir);
        }
        if (document.body && document.body.getAttribute('dir') !== dir) {
          document.body.setAttribute('dir', dir);
        }
        const html = document.querySelector('html');
        if (html && html.getAttribute('dir') !== dir) {
          html.setAttribute('dir', dir);
        }
      }
    }
  }
} catch {}


const renderApp = () => {
  createRoot(document.getElementById('root')!).render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
      </PersistGate>
    </Provider>,
  );
};

// Preload all configured namespaces (plus fallbacks) to avoid keys rendering before HTTP backend finishes
const ns = Array.isArray((i18n.options as any)?.ns) ? (i18n.options as any).ns as string[] : ['common'];
const fallback = ((): string[] => {
  const f = (i18n.options as any)?.fallbackNS;
  if (!f) return [];
  return Array.isArray(f) ? f : [f];
})();

i18n
  .loadNamespaces([...new Set([...ns, ...fallback])])
  .then(renderApp)
  .catch(renderApp)
